import { datadogRum } from '@datadog/browser-rum'

import envConfig, { buildProfile } from '~/configs/env'
import type { BugReportCreateParams } from '~/data/api/client'

type BuildBugReportInput = {
  message: string
  category: string
  pagePath: string
  pageTitle?: string
  pageContext?: string
}

export function splitTitleAndDescription(message: string): { title: string; description: string } {
  const trimmed = message.trim()
  if (!trimmed) {
    return { title: 'Support request', description: '' }
  }

  const lines = trimmed.split('\n').map((line) => line.trim()).filter(Boolean)
  const firstLine = lines[0]
  if (firstLine && lines.length >= 2) {
    return { title: firstLine.slice(0, 200), description: trimmed.slice(0, 8000) }
  }

  const title = trimmed.length > 100 ? `${trimmed.slice(0, 97)}…` : trimmed
  return { title, description: trimmed.slice(0, 8000) }
}

export function buildBugReportParams(input: BuildBugReportInput): BugReportCreateParams {
  const { title, description } = splitTitleAndDescription(input.message)
  const rumContext = typeof datadogRum.getInternalContext === 'function' ? datadogRum.getInternalContext() : undefined
  const connection =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
      : undefined

  return {
    title,
    description,
    category: input.category,
    pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
    pagePath: input.pagePath,
    pageTitle: input.pageTitle ?? (typeof document !== 'undefined' ? document.title : undefined),
    pageContext: input.pageContext,
    datadogSessionReplayUrl:
      typeof datadogRum.getSessionReplayLink === 'function' ? datadogRum.getSessionReplayLink() ?? undefined : undefined,
    datadogSessionId: rumContext?.session_id,
    datadogViewId: rumContext?.view?.id,
    datadogApplicationId: envConfig.datadog?.applicationId,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    browserLanguage: typeof navigator !== 'undefined' ? navigator.language : undefined,
    timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : undefined,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : undefined,
    screenWidth: typeof window !== 'undefined' ? window.screen.width : undefined,
    screenHeight: typeof window !== 'undefined' ? window.screen.height : undefined,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : undefined,
    networkEffectiveType: connection?.effectiveType,
    environment: envConfig.environment,
    buildProfile,
    appVersion: envConfig.appVersion || undefined,
    buildTimestamp: envConfig.buildTimestamp || undefined
  }
}
